import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { MapContainer, TileLayer, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FeedbackManagement } from './FeedbackManagement';
import './Statistics.css';

// Fix for default marker icons in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component to auto-fit map bounds to include circles
function MapBounds({ events }: { events: EventStatistics[] }) {
  const map = useMap();
  const [hasSetBounds, setHasSetBounds] = React.useState(false);

  useEffect(() => {
    if (events.length > 0 && !hasSetBounds) {
      const validEvents = events.filter(
        e => e.finalGatheringLocation?.coordinates?.lat && e.finalGatheringLocation?.coordinates?.lon
      );

      if (validEvents.length > 0) {
        // Create bounds that include the full circles, not just center points
        const allPoints: [number, number][] = [];
        
        validEvents.forEach(e => {
          const lat = e.finalGatheringLocation.coordinates.lat;
          const lon = e.finalGatheringLocation.coordinates.lon;
          const radiusKm = e.maxDistanceKm;
          
          // Calculate approximate lat/lon offsets for the circle radius
          // 1 degree latitude ≈ 111 km
          const latOffset = radiusKm / 111;
          // 1 degree longitude varies with latitude, roughly: 111 * cos(lat)
          const lonOffset = radiusKm / (111 * Math.cos(lat * Math.PI / 180));
          
          // Add points at the circle's edges (north, south, east, west)
          allPoints.push([lat + latOffset, lon]); // North
          allPoints.push([lat - latOffset, lon]); // South
          allPoints.push([lat, lon + lonOffset]); // East
          allPoints.push([lat, lon - lonOffset]); // West
        });
        
        const bounds = L.latLngBounds(allPoints);
        map.fitBounds(bounds, { padding: [50, 50] });
        setHasSetBounds(true);
      }
    }
  }, [events.length, map, hasSetBounds]);

  return null;
}

interface EventStatistics {
  id: string;
  eventId: string;
  generatedAt: string;
  scheduledDate: string;
  organizerId: string;
  finalGatheringLocation: {
    name: string;
    address: string;
    coordinates: {
      lat: number;
      lon: number;
    };
  };
  maxDistanceKm: number;
  courses: {
    appetizer: boolean;
    mainCourse: boolean;
    dessert: boolean;
  };
  schedule: any;
  numberOfPairs: number;
  numberOfUsers: number;
  dietaryRestrictions: { [key: string]: number };
  numberOfMeetsToEat: number;
  totalBikingKms: number;
  numberOfMessagesSent: number;
}

interface LoginStatistics {
  totalUsers: number;
  totalLogins: number;
  loginsByRole: {
    participant: number;
    organizer: number;
    admin: number;
  };
  topUsers: Array<{
    id: string;
    name: string;
    email: string;
    role: string;
    totalLogins: number;
    lastLogin: string;
  }>;
  loginsByDay: { [date: string]: number };
  recentLogins: Array<{
    id: string;
    userId: string;
    role: string;
    email: string;
    timestamp: string;
  }>;
}

const Statistics: React.FC = () => {
  const { accessToken } = useAuth();
  const [statistics, setStatistics] = useState<EventStatistics[]>([]);
  const [loginStats, setLoginStats] = useState<LoginStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'events' | 'logins' | 'feedback'>('events');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    if (activeTab === 'events') {
      fetchStatistics();
    } else {
      fetchLoginStatistics();
    }
  }, [activeTab]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/statistics`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      setStatistics(response.data.statistics || []);
    } catch (err: any) {
      setError('Kunne ikke laste statistikk');
      console.error('Error fetching statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLoginStatistics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/statistics/logins`, {
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      setLoginStats(response.data);
    } catch (err: any) {
      setError('Kunne ikke laste innloggingsstatistikk');
      console.error('Error fetching login statistics:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregate KPIs for events
  const totalEvents = statistics.length;
  const totalUsers = statistics.reduce((sum, s) => sum + s.numberOfUsers, 0);
  const totalPairs = statistics.reduce((sum, s) => sum + s.numberOfPairs, 0);
  const totalKms = statistics.reduce((sum, s) => sum + s.totalBikingKms, 0);
  const totalMeetings = statistics.reduce((sum, s) => sum + s.numberOfMeetsToEat, 0);
  const totalMessages = statistics.reduce((sum, s) => sum + s.numberOfMessagesSent, 0);
  
  // Calculate total meals: each meeting to eat is one meal per person, and we have 2 people per pair
  const totalMeals = statistics.reduce((sum, s) => sum + (s.numberOfMeetsToEat * 2), 0);

  // Calculate total dietary restrictions across all events
  const totalDietaryRestrictions = statistics.reduce((sum, s) => {
    const eventTotal = Object.values(s.dietaryRestrictions).reduce((eventSum, count) => eventSum + count, 0);
    return sum + eventTotal;
  }, 0);

  // Calculate average planning time (days between generation and scheduled date)
  const avgPlanningDays = totalEvents > 0 
    ? statistics.reduce((sum, s) => {
        const generated = new Date(s.generatedAt);
        const scheduled = new Date(s.scheduledDate);
        const diffMs = scheduled.getTime() - generated.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return sum + diffDays;
      }, 0) / totalEvents
    : 0;

  // Calculate averages
  const avgUsersPerEvent = totalEvents > 0 ? (totalUsers / totalEvents).toFixed(1) : 0;
  const avgKmsPerEvent = totalEvents > 0 ? (totalKms / totalEvents).toFixed(1) : 0;
  const avgKmsPerUser = totalUsers > 0 ? (totalKms / totalUsers).toFixed(1) : 0;
  const avgMessagesPerEvent = totalEvents > 0 ? (totalMessages / totalEvents).toFixed(1) : 0;
  const avgDietaryRestrictionsPerEvent = totalEvents > 0 ? (totalDietaryRestrictions / totalEvents).toFixed(1) : 0;

  // Sort by date for timeline
  const sortedStats = [...statistics].sort((a, b) => 
    new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
  );

  // Format date helper
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nb-NO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return <div className="loading">Laster statistikk...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="statistics-container">
      <h2>Statistikk</h2>

      {/* Tabs */}
      <div className="stats-tabs">
        <button
          className={`tab-button ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Arrangementer
        </button>
        <button
          className={`tab-button ${activeTab === 'logins' ? 'active' : ''}`}
          onClick={() => setActiveTab('logins')}
        >
          Innlogginger
        </button>
        <button
          className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          Tilbakemeldinger
        </button>
      </div>

      {activeTab === 'events' && (
        <>
          {statistics.length === 0 ? (
            <div className="empty-state">
              <p>Ingen statistikk tilgjengelig ennå.</p>
              <p>Statistikk genereres fra arrangørens side etter at et arrangement er fullført.</p>
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-icon">🎉</div>
                  <div className="kpi-value">{totalEvents}</div>
                  <div className="kpi-label">Arrangementer</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-icon">👥</div>
                  <div className="kpi-value">{totalUsers}</div>
                  <div className="kpi-label">Deltakere totalt</div>
                  <div className="kpi-sub">Ø {avgUsersPerEvent} per arrangement</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-icon">🚴</div>
                  <div className="kpi-value">{totalKms.toFixed(1)}</div>
                  <div className="kpi-label">Kilometer syklet</div>
                  <div className="kpi-sub">Ø {avgKmsPerUser} km per person</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-icon">🍽️</div>
                  <div className="kpi-value">{totalMeetings}</div>
                  <div className="kpi-label">Måltidsmøter mellom par</div>
                  <div className="kpi-sub">{totalMeals} måltider servert</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-icon">✉️</div>
                  <div className="kpi-value">{totalMessages}</div>
                  <div className="kpi-label">Meldinger sendt</div>
                  <div className="kpi-sub">Ø {avgMessagesPerEvent} per arrangement</div>
                </div>

                <div className="kpi-card">
                  <div className="kpi-icon">🥗</div>
                  <div className="kpi-value">{totalDietaryRestrictions}</div>
                  <div className="kpi-label">Diettbehov totalt</div>
                  <div className="kpi-sub">Ø {avgDietaryRestrictionsPerEvent} per arrangement</div>
                </div>
              </div>

              {/* Map */}
              <div className="statistics-section">
                <h3>Geografisk fordeling</h3>
                <div className="map-container" style={{ height: '500px', width: '100%' }}>
                  <MapContainer
                    center={[60.472, 8.4689]}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapBounds events={statistics} />
                    {statistics
                      .filter(s => s.finalGatheringLocation?.coordinates?.lat && s.finalGatheringLocation?.coordinates?.lon)
                      .map(s => (
                        <Circle
                          key={s.id}
                          center={[s.finalGatheringLocation.coordinates.lat, s.finalGatheringLocation.coordinates.lon]}
                          radius={s.maxDistanceKm * 1000}
                          pathOptions={{ color: '#16a085', fillColor: '#16a085', fillOpacity: 0.2 }}
                        />
                      ))}
                  </MapContainer>
                </div>
              </div>

              {/* Event Timeline Table */}
              <div className="statistics-section">
                <h3>Arrangementsoversikt</h3>
                <div className="table-responsive">
                  <table className="stats-table">
                    <thead>
                      <tr>
                        <th>Dato</th>
                        <th>Sted</th>
                        <th>Deltakere</th>
                        <th>Syklet (km)</th>
                        <th>Møter</th>
                        <th>Meldinger</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedStats.map(s => (
                        <tr key={s.id}>
                          <td>{new Date(s.scheduledDate).toLocaleDateString('nb-NO')}</td>
                          <td>{s.finalGatheringLocation.name}</td>
                          <td>{s.numberOfUsers}</td>
                          <td>{s.totalBikingKms.toFixed(1)}</td>
                          <td>{s.numberOfMeetsToEat}</td>
                          <td>{s.numberOfMessagesSent}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {activeTab === 'logins' && loginStats && (
        <>
          {/* Login KPI Cards */}
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-icon">👥</div>
              <div className="kpi-value">{loginStats.totalUsers}</div>
              <div className="kpi-label">Aktive brukere</div>
              <div className="kpi-sub">Med innloggingshistorikk</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">🔐</div>
              <div className="kpi-value">{loginStats.totalLogins}</div>
              <div className="kpi-label">Totale innlogginger</div>
              <div className="kpi-sub">Siden oppstart</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">👤</div>
              <div className="kpi-value">{loginStats.loginsByRole.participant}</div>
              <div className="kpi-label">Deltaker-innlogginger</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">📊</div>
              <div className="kpi-value">{loginStats.loginsByRole.organizer}</div>
              <div className="kpi-label">Arrangør-innlogginger</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">⚙️</div>
              <div className="kpi-value">{loginStats.loginsByRole.admin}</div>
              <div className="kpi-label">Admin-innlogginger</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon">📈</div>
              <div className="kpi-value">
                {loginStats.totalUsers > 0 ? (loginStats.totalLogins / loginStats.totalUsers).toFixed(1) : 0}
              </div>
              <div className="kpi-label">Ø per bruker</div>
            </div>
          </div>

          {/* Top Users */}
          <div className="statistics-section">
            <h3>Mest aktive brukere (Top 10)</h3>
            <div className="table-responsive">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Navn</th>
                    <th>E-post</th>
                    <th>Rolle</th>
                    <th>Innlogginger</th>
                    <th>Siste innlogging</th>
                  </tr>
                </thead>
                <tbody>
                  {loginStats.topUsers.map(user => (
                    <tr key={user.id}>
                      <td>{user.name || 'N/A'}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role === 'participant' ? 'Deltaker' : user.role === 'organizer' ? 'Arrangør' : 'Admin'}
                        </span>
                      </td>
                      <td>{user.totalLogins}</td>
                      <td>{formatDate(user.lastLogin)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Recent Logins */}
          <div className="statistics-section">
            <h3>Siste innlogginger (100 siste)</h3>
            <div className="table-responsive">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Tidspunkt</th>
                    <th>E-post</th>
                    <th>Rolle</th>
                  </tr>
                </thead>
                <tbody>
                  {loginStats.recentLogins.slice(0, 50).map(login => (
                    <tr key={login.id}>
                      <td>{formatDate(login.timestamp)}</td>
                      <td>{login.email}</td>
                      <td>
                        <span className={`role-badge ${login.role}`}>
                          {login.role === 'participant' ? 'Deltaker' : login.role === 'organizer' ? 'Arrangør' : 'Admin'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'feedback' && (
        <FeedbackManagement />
      )}
    </div>
  );
};

export default Statistics;
